import { z } from 'zod';
import type { components } from './schema.js';
import { montarCaminho, requisitar } from './client.js';
import { erroDeContrato } from './erros.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Produtos: o cadastro sincronizado do ERP e o QR de produto (qr-code.md §15).
 */

const CAMINHO_PRODUTOS = montarCaminho('/produtos');
const CAMINHO_QR = montarCaminho('/produtos/qrcode');

/** Versão 2 fixa (§14.2): 25 × 25 módulos. */
const MODULOS_DA_VERSAO_2 = 25 * 25;

/**
 * A matriz do QR, como o middleware a gerou (`formato=matriz`).
 *
 * Versão, `n` e correção são literais: a etiqueta só sabe desenhar a versão
 * 2-H, e uma resposta de outra versão viraria um QR de módulo menor impresso
 * sem ninguém perceber.
 */
export const qrDoProdutoSchema = z.object({
  codPro: z.string(),
  versao: z.literal(2),
  n: z.literal(25),
  correcao: z.literal('H'),
  modo: z.enum(['numerico', 'alfanumerico', 'byte']),
  mascara: z.number().int().min(0).max(7),
  modulos: z
    .string()
    .length(MODULOS_DA_VERSAO_2)
    .regex(/^[01]+$/),
}) satisfies z.ZodType<components['schemas']['QrProdutoMatrizResponse']>;

export type QrDoProduto = z.infer<typeof qrDoProdutoSchema>;

export const produtoSchema = z.object({
  codPro: z.string(),
  codDer: z.string(),
  nome: z.string(),
  descricaoDerivacao: z.string().nullable(),
  unidadeMedida: z.string(),
  categoria: z.object({ codigo: z.string(), nome: z.string() }).nullable(),
  ativo: z.boolean(),
  controlaLote: z.boolean(),
  excluidoEm: z.string().nullable(),
  atualizadoEm: z.string(),
  sincronizadoEm: z.string(),
}) satisfies z.ZodType<components['schemas']['ProdutoResponse']>;

/** Uma linha do cadastro: produto + derivação. */
export type Produto = z.infer<typeof produtoSchema>;

const paginaDeProdutosSchema = paginaSchema(produtoSchema);

export type FiltroDeProdutos = ParametrosDePagina & {
  /** `codPro` ou trecho do nome. */
  readonly busca?: string;
};

/** Produtos ativos do cadastro sincronizado, pela busca. */
export async function buscarProdutos(filtro: FiltroDeProdutos = {}): Promise<Pagina<Produto>> {
  const corpo = await requisitar(CAMINHO_PRODUTOS, {
    consulta: { ...parametrosDePagina(filtro), busca: filtro.busca },
  });

  return analisar(paginaDeProdutosSchema, corpo, CAMINHO_PRODUTOS);
}

/**
 * A matriz do QR de um `codPro` (`GET /produtos/qrcode?formato=matriz`).
 *
 * A matriz, e não o SVG pronto: o painel posiciona os módulos na etiqueta em
 * milímetros, e um JSON conferido por zod não carrega marcação nenhuma para
 * dentro da página. A query vai escapada (`+`, `&`, `#`, `%`).
 *
 * @throws {ErroApi} `RESPOSTA_INVALIDA` também quando a matriz é de OUTRO
 * `codPro` — a etiqueta escreveria um texto e codificaria outro.
 */
export async function obterQrDoProduto(codPro: string, sinal?: AbortSignal): Promise<QrDoProduto> {
  const corpo = await requisitar(CAMINHO_QR, {
    consulta: { codPro, formato: 'matriz' },
    ...(sinal ? { sinal } : {}),
  });

  const qr = analisar(qrDoProdutoSchema, corpo, CAMINHO_QR);

  if (qr.codPro !== codPro) {
    throw erroDeContrato(CAMINHO_QR);
  }

  return qr;
}
